/**
 * 인앱 결제 — 건전지 후원 (수익모델 §4 · SC-023).
 *
 * `cordova-plugin-purchase` 13.x 를 감싼다. 플러그인이 approved→검증→finish 라이프사이클을
 * 몰고, **HTTP 는 우리가 우리 인증으로 우리 API 에** 보낸다(`store.validator` 의 함수 형태).
 * 그래서 서버 계약은 우리 것이고, 나중에 플러그인을 바꿔도 BE 는 그대로다.
 *
 * ── 왜 validator 함수인가 ──────────────────────────────────────────
 * 플러그인이 넘겨주는 `receipt` 는 **이미 정규화된 본문**이다 — iOS 는 `apple-sk2` +
 * `jwsRepresentation`, Android 는 `android-playstore` + `purchaseToken`. 이걸 직접
 * 꺼내려면 `AppleAppStore.SKTransaction` / `GooglePlay.Receipt` 로 캐스팅해야 하는데
 * (공개 `Transaction` 에는 없다), validator 경로를 쓰면 그 캐스팅이 통째로 사라진다.
 *
 * ── 환경별 동작 ────────────────────────────────────────────────────
 *   비운영 네이티브   플러그인 TEST 어댑터(모의 결제창) → 서버 stub 검증 → finish
 *   비운영 웹         플러그인이 없다 — stub 토큰으로 서버 검증에 바로 보낸다
 *   운영 데스크탑 웹   스토어 없음(영구) — {@link isIapAvailable} 이 false
 *   운영 네이티브     APPLE_APPSTORE / GOOGLE_PLAY
 *
 * ⚠ **`obfuscator` 를 'uuid' 로 명시한다.** 기본값 'legacy' 는 Google 에 raw MD5(32hex)를
 *   보내 Apple 과 값이 갈린다. 'uuid' 는 MD5→UUIDv3 결정론적 변환이라 Apple
 *   `appAccountToken`(UUID 필수)과 Google `obfuscatedAccountId` 에 같은 값이 들어간다.
 *   덕분에 회원 ID→UUID 매핑을 우리가 짤 필요가 없다.
 *
 * ⚠ **`finish()` 는 서버 200 을 받은 뒤에만** 부른다. 먼저 부르면 검증 실패 시 되돌릴 수
 *   없다 — 스토어는 완료로 보고 다시 주지 않는다. 반대로 안 부르면 스토어가 계속
 *   재전달하므로, 실패는 안전한 쪽이다. (플러그인이 소모성에 대해 acknowledge+consume 을
 *   대신 해주므로 Android consume 을 우리가 따로 부르지 않는다.)
 */
import { isNative } from "./index";
import { verifyPurchase, type StorePlatform } from "../api/donation";
import { Capacitor } from "@capacitor/core";
import i18n from "../i18n";
import { DONATION_PRICE_KRW } from "../data/donation";

/** 스토어 상품 ID — 양 스토어 동일. BE 화이트리스트(app.donation.verify.product-ids)와 같은 값. */
export const BATTERY_PRODUCT_ID = "kr.duckflex.peeka.battery.1";

/**
 * ⚠ 플러그인 타입은 **모듈이 아니라 전역 네임스페이스 선언**이다
 * (`declare namespace CdvPurchase`). `import type` 이 안 되므로 삼중 슬래시로 끌어온다.
 * 런타임에도 `globalThis.CdvPurchase` 로만 접근한다 — import 하면 번들에 안 잡힌다.
 */
/// <reference types="cordova-plugin-purchase" />

function store(): any | null {
  const g = globalThis as unknown as { CdvPurchase?: { store?: unknown } };
  return g.CdvPurchase?.store ?? null;
}

/**
 * 스토어 대신 stub 영수증을 보내는가 — **운영 빌드(`--mode prod`)가 아니면 켜진다.**
 *
 * local·dev BE 는 `app.donation.verify.mode: stub` 이라 `stub-` 로 시작하는 토큰만 받는다
 * (StubReceiptVerifier). 네이티브는 플러그인 TEST 어댑터로 결제 흐름(모의 결제창 → 승인 →
 * finish)을 그대로 돌리고, 웹은 플러그인이 없어 서버로 바로 보낸다. 어느 쪽이든 적립·중복
 * 방어·개수 집계·표식까지 실제 서버 경로를 탄다.
 *
 * 실결제(라이선스 테스터) 시험 빌드는 `VITE_IAP_MODE=store` 로 이 분기를 끈다 — 그때는
 * BE 도 `mode: store` 여야 한다(stub 서버는 진짜 토큰을 거부한다).
 * 함수로 두는 이유: MODE 를 호출 시점에 읽어야 테스트가 모드를 바꿀 수 있다.
 */
export function isStubIap(): boolean {
  return import.meta.env.MODE !== "prod" && import.meta.env.VITE_IAP_MODE !== "store";
}

/**
 * 이 환경에서 결제가 가능한가.
 *
 * 운영 데스크탑 웹은 **영구히** false 다 — 스토어가 없다. 화면은 이때 결제 버튼 대신
 * "앱에서 후원할 수 있어요" 를 보여 준다(표식은 서버 값이라 웹에서도 보인다).
 */
export function isIapAvailable(): boolean {
  if (isStubIap()) return true;
  return isNative() && store() !== null;
}

function currentPlatform(): StorePlatform {
  return Capacitor.getPlatform() === "ios" ? "IOS" : "ANDROID";
}

/** stub 서버가 받는 토큰. 거래마다 달라야 한다 — 같은 토큰은 서버가 중복(duplicated)으로 본다. */
function stubToken(transactionId?: string): string {
  const id = transactionId || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `stub-${id}`;
}

export interface IapProduct {
  /** 스토어가 준 로케일 가격 문자열 — ⚠ 이 값을 그대로 그린다. 상수를 그리면 안 된다. */
  localizedPrice: string | null;
}

let initialized = false;

/**
 * 결제 초기화. 후원 시트를 처음 열 때 한 번 부른다.
 *
 * @param memberKey 회원 식별자. 플러그인이 obfuscator 로 UUID 화해 스토어에 실어 보낸다.
 */
export async function initIap(memberKey: string): Promise<IapProduct | null> {
  const s = store();
  // 비운영 웹 — 플러그인이 없다. 가격은 폴백 상수로 그린다.
  if (!s) return isStubIap() ? { localizedPrice: null } : null;

  if (!initialized) {
    const ns = (globalThis as any).CdvPurchase;
    const stub = isStubIap();
    const platform = stub ? ns.Platform.TEST : s.defaultPlatform();
    s.verbosity = s.LogLevel?.WARNING ?? 2;
    s.applicationUsername = memberKey;
    // ⚠ 기본값 'legacy' 는 양 스토어 값이 갈린다. 위 주석 참조.
    s.obfuscator = "uuid";

    s.register([
      {
        id: BATTERY_PRODUCT_ID,
        type: ns.ProductType.CONSUMABLE,
        platform,
        // TEST 어댑터는 우리 상품 ID 를 커스텀 테스트 상품으로 받는다 — 가격은 폴백 상수로.
        ...(stub
          ? {
              title: "Battery (test)",
              pricing: {
                price: `₩${DONATION_PRICE_KRW.toLocaleString("ko-KR")}`,
                currency: "KRW",
                priceMicros: DONATION_PRICE_KRW * 1_000_000,
              },
            }
          : {}),
      },
    ]);

    // 서버 검증 — HTTP 는 우리가 보낸다(우리 인증·우리 DTO).
    // ⚠ TEST 어댑터 영수증에는 이 validator 가 불리지 않는다(플러그인 자체 모의 검증).
    //   그쪽 서버 검증은 purchaseBattery 의 approved 에서 직접 보낸다.
    s.validator = async (receipt: any, callback: (r: unknown) => void) => {
      try {
        const tx = receipt?.transaction ?? {};
        await verifyPurchase({
          platform: currentPlatform(),
          productId: BATTERY_PRODUCT_ID,
          transactionJws: tx.jwsRepresentation,
          purchaseToken: tx.purchaseToken,
          receipt: tx.receipt,
          signature: tx.signature,
        });
        // pending 이어도 ok 다 — 플러그인이 확정 시 다시 검증을 돌린다.
        callback({ ok: true, data: { id: BATTERY_PRODUCT_ID, latest_receipt: true, transaction: tx } });
      } catch (e) {
        // ⚠ ok:false 를 주면 플러그인이 finish 하지 않는다 = 스토어가 재전달한다.
        //   서버 일시 장애(503)에서 이게 정확히 우리가 원하는 동작이다.
        callback({ ok: false, code: 6778001, message: "verify failed" });
      }
    };

    await s.initialize([platform]);
    initialized = true;
  }

  await s.update();
  const product = s.get(BATTERY_PRODUCT_ID);
  return { localizedPrice: product?.pricing?.price ?? null };
}

/**
 * 건전지 하나 넣기.
 *
 * 성공 판정은 **서버 검증까지 끝난 시점**이다 — 스토어 결제만 끝난 상태로 축하를 띄우면,
 * 검증이 실패했을 때 넣지도 않은 건전지를 보여 주게 된다.
 *
 * @returns 검증 후 건전지 수. 사용자가 취소하면 null.
 */
export async function purchaseBattery(): Promise<number | null> {
  const s = store();
  if (!s) {
    if (!isStubIap()) return null;
    // 비운영 웹 — 플러그인이 없어 결제 단계 없이 서버 검증으로 바로 간다.
    const result = await verifyPurchase({
      platform: currentPlatform(),
      productId: BATTERY_PRODUCT_ID,
      purchaseToken: stubToken(),
    });
    return result.batteryCount;
  }

  const offer = s.get(BATTERY_PRODUCT_ID)?.getOffer();
  if (!offer) throw new Error(i18n.t("me:donation.offerFailed"));
  const ns = (globalThis as any).CdvPurchase;
  const testPlatform = ns?.Platform?.TEST;

  return new Promise<number | null>((resolve, reject) => {
    let settled = false;
    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    s.when()
      .approved(async (tx: any) => {
        if (tx.platform === testPlatform) {
          // TEST 어댑터는 validator 를 건너뛰므로 서버 검증을 여기서 보낸다.
          // ⚠ 200 을 받은 뒤에만 verify → finish 로 넘긴다. 실패하면 finish 하지 않는다.
          try {
            await verifyPurchase({
              platform: currentPlatform(),
              productId: BATTERY_PRODUCT_ID,
              purchaseToken: stubToken(tx.transactionId),
            });
          } catch (e) {
            done(() => reject(e));
            return;
          }
        }
        void tx.verify();
      })
      .verified((receipt: any) => {
        // ⚠ 서버 200 뒤에만 finish. 위 주석 참조.
        void receipt.finish();
        done(() => resolve(-1)); // 개수는 react-query 가 서버에서 다시 읽는다
      })
      .unverified(() => done(() => reject(new Error(i18n.t("me:donation.failed")))));

    // ⚠ v13 의 when() 에는 cancelled 가 없다. 취소·실패는 order() 가 **resolve 값(IError)** 으로
    //   돌려준다. 예전 코드는 .cancelled() 를 불러 TypeError 로 결제창조차 못 열었다(2026-09-11 실기기).
    void offer
      .order()
      .then((err: any) => {
        if (!err) return; // 진행 중 — approved → verified 가 마무리한다
        if (err.code === ns?.ErrorCode?.PAYMENT_CANCELLED) done(() => resolve(null));
        else done(() => reject(new Error(err.message ?? "order failed")));
      })
      .catch((e: unknown) => done(() => reject(e)));
  });
}
